import clsx from 'clsx'

interface HealthGaugeProps {
  value: number // 0-100
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
  label?: string
  className?: string
}

export default function HealthGauge({
  value,
  size = 'md',
  showLabel = true,
  label = 'Health',
  className
}: HealthGaugeProps) {
  // Clamp value between 0 and 100
  const clampedValue = Math.max(0, Math.min(100, value))

  // Determine status based on value
  const getStatus = () => {
    if (clampedValue >= 85) return 'healthy'
    if (clampedValue >= 60) return 'warning'
    return 'critical'
  }

  const status = getStatus()

  const statusColors = {
    healthy: {
      stroke: '#10b981',
      bg: 'bg-status-healthy/10',
      text: 'text-status-healthy'
    },
    warning: {
      stroke: '#f59e0b',
      bg: 'bg-status-warning/10',
      text: 'text-status-warning'
    },
    critical: {
      stroke: '#ef4444',
      bg: 'bg-status-critical/10',
      text: 'text-status-critical'
    }
  }

  const sizes = {
    sm: { container: 'w-16 h-16', text: 'text-sm', label: 'text-xs' },
    md: { container: 'w-24 h-24', text: 'text-xl', label: 'text-xs' },
    lg: { container: 'w-32 h-32', text: 'text-2xl', label: 'text-sm' }
  }

  const sizeConfig = sizes[size]
  const colorConfig = statusColors[status]

  // SVG circle calculations
  const strokeWidth = size === 'sm' ? 4 : size === 'md' ? 5 : 6
  const radius = size === 'sm' ? 28 : size === 'md' ? 42 : 56
  const circumference = 2 * Math.PI * radius
  const offset = circumference - (clampedValue / 100) * circumference

  return (
    <div className={clsx('relative inline-flex flex-col items-center', className)}>
      <div className={clsx('relative', sizeConfig.container)}>
        <svg className="w-full h-full transform -rotate-90">
          {/* Background circle */}
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            fill="none"
            stroke="currentColor"
            strokeWidth={strokeWidth}
            className="text-surface-700"
          />
          {/* Progress circle */}
          <circle
            cx="50%"
            cy="50%"
            r={radius}
            fill="none"
            stroke={colorConfig.stroke}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            className="transition-all duration-500 ease-out"
          />
        </svg>
        {/* Center value */}
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={clsx('font-mono font-bold', sizeConfig.text, colorConfig.text)}>
            {Math.round(clampedValue)}
          </span>
        </div>
      </div>
      {showLabel && (
        <span className={clsx('mt-2 text-surface-400', sizeConfig.label)}>
          {label}
        </span>
      )}
    </div>
  )
}
